<template>
  <CCard>
    <CCardHeader>
      <CIcon name="cil-notes"/>
      <h5 class="d-inline ml-2">
        {{ this.$route.meta.label }}
        <template>
          : <h3 class="d-inline"><CBadge color="secondary">{{ this.itemName }}</CBadge></h3>
        </template>
      </h5>
      <div class="card-header-actions">
        <CButton
          color="info"
          class="mr-2"
          :to="{ name: 'User', params: { id: itemId } }"
        >
          <CIcon name="cil-user" class="align-bottom" /> View User
        </CButton>
        <CButton color="dark" :to="{ name: 'All Users'}">
          <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <CForm>
        <CRow>
          <CCol lg="12">
            <div class="form-group w-100">
              <label class="d-block">User Type:</label>
              <CInputRadioGroup
                name="usertype"
                :options="['User', 'Admin', 'Banned']"
                :checked.sync="$v.form.usertype.$model"
                custom
                inline
              />
            </div>
          </CCol>

          <CCol lg="6">
            <div class="form-group w-100">
              <label class="d-block">Roles:</label>
              <v-select
                v-model="$v.form.roles.$model"
                :options="allRoles"
                placeholder="No role selected!"
                label="name"
                :disabled="form.usertype !== 'Admin'"
                :searchable="false"
                multiple
              >
                <template v-slot:option="roles">
                  <span>{{ roles.name }}</span>
                </template>
              </v-select>
            </div>
          </CCol>

          <CCol lg="6">
            <CInput
              class="w-100"
              label="2FA Mobile Number:"
              :lazy="false"
              :value.sync="$v.form.tfamobile.$model"
            >
              <template #prepend-content><CIcon name="cil-mobile" /></template>
            </CInput>
          </CCol>

          <CCol lg="6" v-if="form.usertype === 'Admin'">
            <CInput
              class="w-100"
              label="VoIP Extension:"
              placeholder="e.g., 2001, 2002"
              :lazy="false"
              :value.sync="$v.form.voip_extension.$model"
            >
              <template #prepend-content><CIcon name="cil-phone" /></template>
            </CInput>
          </CCol>

          <CCol lg="12">
            <hr>
            <h6>Marketing Email</h6>
          </CCol>

          <CCol lg="4">
            <div class="form-group w-100">
              <label class="d-block">Email Subscribe:</label>
              <CSwitch
                :checked.sync="$v.form.email_subscribe.$model"
                labelOn="YES"
                labelOff="NO"
                color="success"
                shape="pill"
                size="lg"
              />
            </div>
          </CCol>

          <CCol lg="4">
            <div class="form-group w-100">
              <label class="d-block">Internal Marketing Block:</label>
              <CSwitch
                :checked.sync="$v.form.internal_blocked.$model"
                labelOn="YES"
                labelOff="NO"
                color="danger"
                shape="pill"
                size="lg"
              />
            </div>
          </CCol>

          <CCol lg="4">
            <div class="form-group w-100">
              <label class="d-block">Effective Marketing Status:</label>
              <CBadge :color="effectiveMarketingStatus ? 'success' : 'secondary'" class="p-2">
                {{ effectiveMarketingStatus ? 'Subscribed' : 'Blocked / Unsubscribed' }}
              </CBadge>
            </div>
          </CCol>
        </CRow>
      </CForm>
    </CCardBody>

    <CCardFooter>
      <CButton
        color="primary"
        :disabled="submitted"
        @click="update"
      >
        Submit
      </CButton>
    </CCardFooter>

    <CElementCover :opacity="0.4" v-show="submitted"/>
  </CCard>

</template>

<script>
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate"
import { required } from "vuelidate/lib/validators"
import { Role } from "../../../router/role";

export default {
  name: 'EditUser',
  data() {
    return {
      form: {},
      submitted: false,

      itemName: '',
      allRoles: [],
    }
  },

  watch: {
    'form.usertype': function(newVal) {
      if (newVal !== 'Admin') {
        this.form.roles = []
        this.form.voip_extension = null
      }
    },
  },

  async created () {
    this.form = await this.getForm(this.itemId)
    this.allRoles = Object.keys(Role);
  },

  computed: {
    ...mapGetters(["errors"]),

    isValid () { return !this.$v.form.$invalid },
    isDirty () { return this.$v.form.$anyDirty },
    itemId() { return this.$route.params.id },
    effectiveMarketingStatus () {
      return this.form.email_subscribe === true && this.form.internal_blocked !== true
    }
  },

  mixins: [validationMixin],

  validations: {
    form: {
      usertype: { required },
      tfamobile: { },
      roles: { },
      voip_extension: { },
      email_subscribe: { },
      internal_blocked: { },
    }
  },

  methods: {
    checkIfValid (fieldName) {
      const field = this.$v.form[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === '')
    },

    async update () {
      if (!this.isValid) {
        this.validate();
        return;
      }

      this.submitted = true

      try {
        const response = await this.$axios.put(this.$backend.USERS.UPDATE.replace("{id}", this.itemId), this.form)

        await this.$axios.patch(
          this.$backend.USERS.UPDATE_EMAIL_SUBSCRIPTION.replace("{id}", this.itemId),
          {
            email_subscribe: this.form.email_subscribe,
            internal_blocked: this.form.internal_blocked,
          }
        )

        this.itemName = response.data.first_name
        this.$toast.success(`${this.itemName} updated.`)
      } catch (e) {
        this.$toast.error(typeof this.errors === 'object'
          ? Object.values(this.errors).join()
          : this.errors
        );
      } finally {
        this.submitted = false
      }
    },

    validate () {
      //validate the form
      this.$v.$touch()
    },

    async getForm(id) {
      var form = {
        usertype: "",
        tfamobile: null,
        roles: [],
        voip_extension: null,
        email_subscribe: false,
        internal_blocked: false,
      }

      this.submitted = true
      await this.$axios.get(this.$backend.USERS.SHOW.replace("{id}", id))
        .then((res) => {
          form.usertype = res.data.usertype;
          form.tfamobile = res.data.tfamobile;
          form.roles = res.data.roles.map(r => { return r.name });
          form.voip_extension = res.data.voip_extension;
          form.email_subscribe = res.data.email_subscribe == true || res.data.email_subscribe === 1;
          form.internal_blocked = !!res.data.email_marketing_blocked_at;

          this.itemName = res.data.first_name;
        })
        .catch(() => {
          this.$router.push({ name: 'All Users' })
        })
        .finally(() => this.submitted = false);

      return form;
    }
  }
}
</script>
